import { useCallback, useState } from 'react';
import { approveFriendRequest, fetchPendingRequestCount, fetchReceivedFriendRequests, rejectFriendRequest } from '@domain/contacts/api';
import type { FriendRequest } from '@domain/contacts/types';
import { getErrorMessage } from '@shared/utils/ui';

type UseFriendRequestsOptions = {
  apiBase: string;
  isAuthenticated: boolean;
  pushLog: (msg: string, kind?: 'info' | 'in' | 'out' | 'error' | 'success' | 'warn') => void;
  onApproved?: () => Promise<void>;
};

export function useFriendRequests({
  apiBase,
  isAuthenticated,
  pushLog,
  onApproved,
}: UseFriendRequestsOptions) {
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [friendRequestsLoading, setFriendRequestsLoading] = useState(false);
  const [friendRequestActionById, setFriendRequestActionById] = useState<Record<number, 'approve' | 'reject'>>({});
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  const resetFriendRequestsState = useCallback(() => {
    setFriendRequests([]);
    setFriendRequestsLoading(false);
    setFriendRequestActionById({});
    setPendingRequestCount(0);
  }, []);

  const loadFriendRequests = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    setFriendRequestsLoading(true);
    try {
      const requests = await fetchReceivedFriendRequests(apiBase);
      setFriendRequests(requests);
    } catch (error) {
      setFriendRequests([]);
      console.warn('Friend request API not available:', error);
    } finally {
      setFriendRequestsLoading(false);
    }
  }, [apiBase, isAuthenticated]);

  const refreshPendingCount = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const count = await fetchPendingRequestCount(apiBase);
      setPendingRequestCount(count);
    } catch (error) {
      setPendingRequestCount(0);
      console.warn('Friend request count API not available:', error);
    }
  }, [apiBase, isAuthenticated]);

  const handleApproveRequest = useCallback(async (request: FriendRequest) => {
    setFriendRequestActionById((prev) => ({ ...prev, [request.requestId]: 'approve' }));
    try {
      await approveFriendRequest(request.requestId, apiBase);
      setFriendRequests((prev) => prev.filter((entry) => entry.requestId !== request.requestId));
      pushLog(`已通过 ${request.fromUserName || request.fromUserEmail} 的关系申请`, 'success');
      await Promise.all([refreshPendingCount(), loadFriendRequests(), onApproved?.()]);
    } catch (error) {
      pushLog(`通过关系申请失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setFriendRequestActionById((prev) => {
        const next = { ...prev };
        delete next[request.requestId];
        return next;
      });
    }
  }, [apiBase, loadFriendRequests, onApproved, pushLog, refreshPendingCount]);

  const handleRejectRequest = useCallback(async (request: FriendRequest) => {
    setFriendRequestActionById((prev) => ({ ...prev, [request.requestId]: 'reject' }));
    try {
      await rejectFriendRequest(request.requestId, apiBase);
      setFriendRequests((prev) => prev.filter((entry) => entry.requestId !== request.requestId));
      pushLog(`已拒绝 ${request.fromUserName || request.fromUserEmail} 的关系申请`, 'warn');
      await Promise.all([refreshPendingCount(), loadFriendRequests()]);
    } catch (error) {
      pushLog(`拒绝关系申请失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setFriendRequestActionById((prev) => {
        const next = { ...prev };
        delete next[request.requestId];
        return next;
      });
    }
  }, [apiBase, loadFriendRequests, pushLog, refreshPendingCount]);

  return {
    friendRequests,
    friendRequestsLoading,
    friendRequestActionById,
    pendingRequestCount,
    resetFriendRequestsState,
    loadFriendRequests,
    refreshPendingCount,
    handleApproveRequest,
    handleRejectRequest,
  };
}
